import React, { useState } from "react";
import Carousel from "react-bootstrap/Carousel";
import HeroSkeleton from "../pages/skeletons/hero";
import { ArrowRight, Sparkles } from "lucide-react";
import Button from "../common/Button";

const pic = {
  url: "https://res.cloudinary.com/dkcjpdk1c/image/upload/v1767213799/Codehub_hero_background_jvu1gt.jpg",
};

const theme = {
  bgGrainOverlay: "#1E293B",
  bgGrain:
    "https://res.cloudinary.com/dkcjpdk1c/image/upload/v1767214012/sqaure-codehub_qedq9g.png",
};

export const layeredBackground: React.CSSProperties = {
  backgroundColor: `${theme.bgGrainOverlay}`,
  backgroundImage: `
      linear-gradient(transparent 10px, ${theme.bgGrainOverlay} 800px), 
      url("${theme.bgGrain}"),  
      url("${pic.url}")
    `,
  backgroundPosition: "top, top, top, center",
  backgroundSize: "100% 100%, 75px, 100% 1150px, cover",
  backgroundRepeat: "no-repeat, repeat, no-repeat, no-repeat",
  backgroundBlendMode: "normal, overlay, normal",
  minHeight: "480px",
};

interface HeroItem {
  id: number | string;
  title: string;
  hero_snippet: string;
  get_hero_image_url?: string;
  slug?: string;
}

interface HeroSectionProps {
  heroList: HeroItem[];
  serviceLoading: boolean;
}

const HeroSlideItem: React.FC<{ item: HeroItem }> = ({ item }) => {
  const [isPortrait, setIsPortrait] = useState<boolean>(false);

  return (
    <div
      style={layeredBackground}
      className="cht-w-full cht-min-h-[480px] sm:cht-min-h-[520px] lg:cht-min-h-[580px] cht-flex cht-items-center"
    >
      <div className="cht-max-w-7xl cht-mx-auto cht-px-6 sm:cht-px-8 lg:cht-px-12 cht-w-full cht-pt-12 sm:cht-pt-16 cht-pb-16 sm:cht-pb-20 md:cht-pb-12">
        <div className="cht-grid md:cht-grid-cols-12 cht-gap-8 lg:cht-gap-12 cht-items-center">
          {/* Left Text Column: 7 cols on md, 6 cols on lg */}
          <div className="md:cht-col-span-7 lg:cht-col-span-6 cht-space-y-4 cht-text-left cht-z-10">
            <h1 className="!cht-font-heading cht-text-2xl sm:cht-text-4xl md:cht-text-5xl lg:cht-text-6xl !cht-font-extrabold cht-text-white cht-tracking-tight !cht-leading-[1.18]">
              {item.title}
            </h1>

            <p className="!cht-font-body cht-text-sm sm:cht-text-base md:cht-text-lg cht-text-slate-200/90 cht-leading-relaxed cht-max-w-xl">
              {item.hero_snippet}
            </p>

            <div className="cht-pt-2 cht-flex cht-flex-wrap cht-items-center cht-gap-3">
              <Button
                to={item.slug ? `/services/${item.slug}` : "/services"}
                variant="primary"
                size="md"
                rightIcon={<ArrowRight size={15} />}
              >
                Explore Capabilities
              </Button>

              <Button
                href="#contact-form"
                variant="secondary"
                size="md"
                className="cht-bg-white/10 hover:cht-bg-white/20 !cht-text-white cht-border cht-border-white/20 cht-backdrop-blur-sm"
              >
                Request Scope Quote
              </Button>
            </div>
          </div>

          {/* Right Image Showcase Column: 5 cols on md, 6 cols on lg */}
          <div className="md:cht-col-span-5 lg:cht-col-span-6 cht-flex cht-items-center cht-justify-center md:cht-justify-end cht-min-w-0">
            {item.get_hero_image_url && (
              <div className="cht-relative group cht-w-full cht-flex cht-items-center cht-justify-center md:cht-justify-end cht-min-w-0">
                {/* Ambient Lighting Backdrop */}
                <div className="cht-absolute -cht-inset-4 cht-bg-gradient-to-r cht-from-blue-600/20 cht-to-indigo-500/20 cht-rounded-full cht-blur-3xl cht-opacity-50 group-hover:cht-opacity-80 cht-transition-opacity -cht-z-10" />

                <img
                  src={item.get_hero_image_url}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalHeight && img.naturalWidth) {
                      setIsPortrait(img.naturalHeight > img.naturalWidth);
                    }
                  }}
                  style={
                    isPortrait
                      ? {
                          WebkitMaskImage:
                            "linear-gradient(to bottom, black 82%, transparent 100%)",
                          maskImage:
                            "linear-gradient(to bottom, black 82%, transparent 100%)",
                        }
                      : undefined
                  }
                  className={`cht-max-w-full cht-w-auto cht-h-auto ${
                    isPortrait
                      ? "cht-max-h-[320px] sm:cht-max-h-[380px] md:cht-max-h-[440px] lg:cht-max-h-[480px]"
                      : "cht-max-h-[260px] sm:cht-max-h-[320px] md:cht-max-h-[360px] lg:cht-max-h-[400px]"
                  } cht-object-contain cht-drop-shadow-[0_20px_35px_rgba(0,0,0,0.5)] group-hover:cht-scale-[1.02] cht-transition-transform cht-duration-500`}
                  alt={item.title || "Hero Showcase"}
                  loading="eager"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection: React.FC<HeroSectionProps> = ({
  heroList,
  serviceLoading,
}) => {
  const [index, setIndex] = useState(0);

  const handleSelect = (selectedIndex: number) => {
    setIndex(selectedIndex);
  };

  if (serviceLoading) {
    return <HeroSkeleton />;
  }

  if (!Array.isArray(heroList) || heroList.length === 0) {
    return null;
  }

  return (
    <div className="cht-relative cht-w-full cht-overflow-hidden">
      <Carousel
        activeIndex={index}
        onSelect={handleSelect}
        fade
        interval={12000}
        indicators={false}
        className="hero-carousel"
      >
        {heroList.map((item) => (
          <Carousel.Item key={item.id} className="cht-relative">
            <HeroSlideItem item={item} />
          </Carousel.Item>
        ))}
      </Carousel>

      {/* Modern Expanding Dots Indicator */}
      {heroList.length > 1 && (
        <div
          className="cht-absolute cht-bottom-4 sm:cht-bottom-6 cht-left-0 cht-right-0 cht-z-20 cht-flex cht-items-center cht-justify-center cht-gap-2.5 cht-pointer-events-auto"
          role="tablist"
          aria-label="Carousel pagination"
        >
          {heroList.map((item, i) => {
            const isActive = index === i;
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleSelect(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`cht-h-2.5 cht-rounded-full cht-transition-all cht-duration-300 cht-ease-out cht-cursor-pointer ${
                  isActive
                    ? "cht-w-8 cht-bg-[#3e57da] cht-shadow-[0_0_14px_rgba(62,87,218,0.85)] cht-border cht-border-white/60"
                    : "cht-w-2.5 cht-bg-white/40 hover:cht-bg-white/70"
                }`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeroSection;
